import { useState } from "react";
import { useTranslation } from "../../hooks/useTranslation";
import { useLanguage } from "../../hooks/useLanguage";
import { Project } from "./content/model";
import { Title } from "../../components/ui/title";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Button } from "../../components/ui/button";

import styles from "./index.module.scss";

import frenchJson from './content/fr.json';
import englishJson from './content/en.json';

import redCross from "../../assets/icons/red-cross.svg";
import mountains from "../../assets/illustrations/mountains.svg"
import line_fr from "../../assets/illustrations/laptop/line/fr.svg";
import line_en from "../../assets/illustrations/laptop/line/en.svg";
import recipe_fr from "../../assets/illustrations/laptop/recipe/fr.svg";
import recipe_en from "../../assets/illustrations/laptop/recipe/en.svg";
import laptop from "../../assets/illustrations/laptop/laptop.svg";

interface PortfolioProps {
    id?: string;
}

export const Portfolio = ({ id }: PortfolioProps) => {
    const { t } = useTranslation();
    const { language } = useLanguage();

    const content: Project[] = language === 'fr' ? (frenchJson as Project[]) : (englishJson as Project[]);

    return (
        <div className={styles.mountains} id={id}>

            <img className={styles.mountainsImage} src={mountains} alt="Mountains" />

            <div className={styles.portfolioPage}>

                <Title color="white">{t('header.portfolio')}</Title>
                <ProjectContent content={content} />

            </div>
        </div>
    );
}

type ProjectProps = {
    content: Project[];
}

const ProjectContent = (props: ProjectProps) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    const contentLength = props.content.length+1;
    const [ projectIndex, setProjectIndex ] = useState(0);

    const buttons = (
    <div className={styles.buttons}>
        <button
            className={styles.button}
            onClick={() => setProjectIndex((projectIndex - 1 + contentLength) % contentLength)}
        >
            <h3>←</h3>
        </button>
        <button
            className={styles.button}
            onClick={() => setProjectIndex((projectIndex + 1) % contentLength)}
        >
            <h3>→</h3>
        </button>
    </div>
    );

    if (projectIndex === contentLength - 1) {
        return (
            <div className={styles.project}>

                <div className={styles.description}>

                    <div className={styles.details}>
                        <h3 className={styles.title}>{t("portfolio.andYou")}</h3>
                        <h3>{t("portfolio.question")}</h3>
                    </div>

                    <Button isFullWidth onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}>
                        {t("global.contact")}
                    </Button>
                    
                    {buttons}
                </div>
                
            </div>
        );
    } 


    return (
        <div className={styles.project}>

            <div className={styles.description}>

                <div className={styles.details}>
                    <h3 className={styles.title}>{t(props.content[projectIndex].type)}</h3>
                    <h3>{t(props.content[projectIndex].institution)}</h3>
                    <p>{t(props.content[projectIndex].location)}</p>
                </div>

                <div className={styles.subPart}>
                    <p className={styles.subTitle}>{t('portfolio.functionalities')}</p>
                    {props.content[projectIndex].functionalities.map((functionality, index) => (
                        <div className={styles.bullet} key={index}>
                            <img src={redCross} alt="Red Cross" />
                            <p>{t(functionality)}</p>
                        </div>
                    ))}

                </div>

                <div className={styles.subPart}>
                    <p className={styles.subTitle}>{t('portfolio.goals')}</p>
                    {props.content[projectIndex].goals.map((goal, index) => (
                        <div className={styles.bullet} key={index}>
                            <img src={redCross} alt="Red Cross" />
                            <p>{t(goal)}</p>
                        </div>
                    ))}
                </div>

                <div className={styles.subPart}>
                    <p className={styles.subTitle}>{t('portfolio.deliverables')}</p>
                    {props.content[projectIndex].deliverables.map((deliverable, index) => (
                        <div className={styles.bullet} key={index}>
                            <img src={redCross} alt="Red Cross" />
                            <p>{t(deliverable)}</p>
                        </div>
                    ))}
                </div>

                {!isMobile && buttons}
            </div>

            <LaptopFactory image={props.content[projectIndex].image} />

            {isMobile && buttons}
        </div>
    );
}

type LaptopFactoryProps = {
    image: string;
}

const LaptopFactory = (props: LaptopFactoryProps) => {
    const { language } = useLanguage();

    switch (props.image) {
        case 'line':
            if(language === 'fr') {
                return <img className={styles.laptop} src={line_fr} alt="Illustration" />
            } else {
                return <img className={styles.laptop} src={line_en} alt="Illustration" />
            }
        case 'recipe':
            if(language === 'fr') {
                return <img className={styles.laptop} src={recipe_fr} alt="Illustration" />
            } else {
                return <img className={styles.laptop} src={recipe_en} alt="Illustration" />
            }
        default :
            return <img className={styles.laptop} src={laptop} alt="Illustration" />
    }

}